import getMenuItems from 'util/get-menu-items';

import type React from 'react';
import { useCallback } from 'react';

import Arrow from 'assets/images/icons/Arrow.png';
import offline_status from 'assets/images/icons/offline_status1.png';
import online_status from 'assets/images/icons/online_status.png';
import { CalenderIcon, ChatIcon } from 'assets/svg-icons';
import { useCall } from 'context/call-context';
import { isNullOrEmpty } from 'helper/is-null-or-empty';
import { renderPrimaryIconBaseOnBaseUrl } from 'helper/popup-chat/popup-chat-helper';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';
import { t } from 'i18next';
import type { MenuOption, OtherWidget, WidgetOption } from 'types';
import { MenuType } from 'types';

import classes from './chat-bubble-side-bar-style.module.scss';

interface ICollapsedBody {
  isChatEnabled: boolean;
  isLeftPosiotion: boolean;
  onlineMenu: MenuOption[];
  offlineMenu: MenuOption[];
  onlineButton: boolean;
  offlineButton: boolean;
  otherWidgets: OtherWidget[];
  otherWidgetsRef: React.MutableRefObject<Map<string, boolean>>;
  schedulingOption: { enabled: boolean };
  widgetOption: WidgetOption;
  onBubbleButtonClick: () => void;
  onPressOfflineLink: () => void;
  onAppointmentClick: () => void;
  onClickToggle: () => void;
  onClickAvatar: () => void;
  setOnlineButton: (value: boolean) => void;
  setOfflineButton: (value: boolean) => void;
  toggleOtherWidget: (value: OtherWidget) => void;
}
// eslint-disable-next-line sonarjs/cognitive-complexity
const CollapsedSideBarBody = ({
  isLeftPosiotion,
  isChatEnabled,
  onlineMenu,
  offlineMenu,
  onlineButton,
  offlineButton,
  otherWidgets,
  otherWidgetsRef,
  schedulingOption,
  widgetOption,
  onBubbleButtonClick,
  onPressOfflineLink,
  onAppointmentClick,
  onClickAvatar,
  onClickToggle,
  setOnlineButton,
  setOfflineButton,
  toggleOtherWidget
}: // eslint-disable-next-line sonarjs/cognitive-complexity
ICollapsedBody) => {
  const { color } = widgetOption;
  const iconSize = '16px';
  const iconColor = !isChatEnabled ? '#c7c7c7' : '#000000';
  const { productType } = useCall();
  const { log } = useActivityLogger();

  const renderPrimaryButtons = () => (
    <button
      id="conferwith-start-video-call"
      aria-label="video call buttons"
      onClick={onBubbleButtonClick}
      style={{ display: isChatEnabled ? 'block' : 'none' }}
      className={classes.cwPrimaryBtnOnCollapesed}
    >
      {renderPrimaryIconBaseOnBaseUrl({ primaryTextColor: iconColor, styles: { display: 'block' } })}
    </button>
  );

  const renderMenuButton = (data: MenuOption, isOnline: boolean) => {
    const { externalFunction, link } = getMenuItems(data, isOnline ? onlineButton : offlineButton);
    if (!isNullOrEmpty(productType) && !data.enabled) {
      return;
    }
    const buttonAction = () => {
      void log({
        action: Action.ON_SECONDARY_BUTTON_CLICKED,
        description: `customer clicked secondary button`,
        index: Index.ACTIVITY,
        label: 'CUSTOMER_WIDGET'
      });

      if (link) {
        window.location.href = link || '';
        return;
      }
      if (externalFunction) {
        externalFunction();
      }
      if (data.data.type !== MenuType.JSON) return;
      if (isOnline) {
        setOnlineButton(!onlineButton);
        return;
      }
      onPressOfflineLink();
      setOfflineButton(!offlineButton);
    };

    return (
      <button
        aria-label={`${data.data.icon} button`}
        key={data.data.icon}
        onClick={buttonAction}
      >
        <i
          className={`${data.data.icon} ${isOnline ? '' : classes.cwButtonIcons}`}
          style={{ fontSize: iconSize }}
        />
      </button>
    );
  };

  const renderSecondaryButtons = () => {
    if (isChatEnabled) {
      return onlineMenu.filter((data) => !data.data.hideMinimizedIcon).map((data) => renderMenuButton(data, true));
    }
    return offlineMenu.filter((data) => !data.data.hideMinimizedIcon).map((data) => renderMenuButton(data, false));
  };

  const renderOtherWidget = useCallback(
    () =>
      otherWidgets &&
      otherWidgets
        .filter(
          (otherWidget) =>
            otherWidget?.label &&
            ((otherWidget.id && otherWidgetsRef.current.get(otherWidget.id) !== undefined) ||
              (otherWidget.function && otherWidgetsRef.current.get(otherWidget.function) !== undefined))
        )
        .map((otherWidget) => (
          <button
            aria-label={`chat button`}
            key={otherWidget.id}
            onClick={() => toggleOtherWidget(otherWidget)}
            style={{
              border: `2px solid ${color}`,
              display: otherWidget?.label?.trim() === '' || !otherWidget?.label ? 'none' : 'flex'
            }}
            className={classes.cwSecondaryBtn}
          >
            <ChatIcon color="#000000" />
          </button>
        )),
    [otherWidgetsRef, otherWidgets]
  );

  return (
    <div className={classes.cwOnCollapsedBody}>
      <div
        role="button"
        onClick={onClickAvatar}
        aria-label={`image call button`}
        className={classes.cwStatus}
        style={{ color: isChatEnabled ? 'rgb(160, 191, 110)' : '#888888' }}
      >
        <img
          src={isChatEnabled ? online_status : offline_status}
          alt="offline and online status image"
        />
        {/* Text: Live */}
        {isChatEnabled && <span>{t('chatBubbleV2.live')}</span>}
      </div>
      <div className={classes.cwButtonWrapper}>
        <div className={classes.cwOnColBtn}>
          {renderPrimaryButtons()}
          {/* Online and offline Menu Buttons */}
          <div className={classes.cwSecondayBtnWrapper}>
            {!isChatEnabled ||
              (schedulingOption.enabled && (
                <button
                  onClick={onAppointmentClick}
                  aria-label={`book button`}
                >
                  <CalenderIcon
                    style={{ display: 'flex', justifyContent: 'center' }}
                    color={iconColor}
                  />
                </button>
              ))}
            {renderSecondaryButtons()}
            {/* OtherWidget Menu Buttons */}
            {renderOtherWidget()}
          </div>
          <span
            role="button"
            aria-label={`arrow button`}
            onClick={onClickToggle}
            className={classes.widgetCurveBtnOnCollapsed}
          >
            <img
              className={isLeftPosiotion ? classes.left : classes.right}
              src={Arrow}
              alt="arrow icon"
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default CollapsedSideBarBody;
